import * as Cesium from 'cesium'

export default {
  /**
   * Compute the shift to be applied to the positions array to visualize intra and inter plane phasings
   * @param numberOfPointsPerTrace - the length of the positions array
   * @param planeIndex - the index of the plane used to compute interplane shift
   * @param satIndex - the index of the satellite used to compute intraplane shift
   * @param intraPlanePhasing - the intra-plane phasing of the constellation in radians
   * @param interPlanePhasing - the inter-plane phasing of the constellation in radians
   * @returns the computed total shift as an index of the positions array
   */
  computeShift(
    numberOfPointsPerTrace,
    planeIndex,
    satIndex,
    intraPlanePhasing,
    interPlanePhasing
  ) {
    if (isNaN(interPlanePhasing)) {
      interPlanePhasing = 0
    }
    const totalShiftInRadians =
      satIndex * intraPlanePhasing + planeIndex * interPlanePhasing

    const clampedTotalShiftInRadians =
      Cesium.Math.zeroToTwoPi(totalShiftInRadians)

    const totalShiftInDegrees = Cesium.Math.toDegrees(
      clampedTotalShiftInRadians
    )

    const totalShift = Math.floor(
      (totalShiftInDegrees * numberOfPointsPerTrace) / 360
    )

    return totalShift
  },

  /**
   * Create a time sampled positions array to represent the position of a moving satellite
   * @param posArray - the array containing the positions of the orbital plane
   * @param totalShift - the shift corresponding the intra and inter plane phasings
   * @param timeStep - the time step in seconds between two positions
   * @returns the time sampled positions array
   */
  createShiftedPosArray(posArray, totalShift, timeStep) {
    let shiftedPosArray = []
    let timeCounter = 0
    let epoch = 0
    let subArr = []

    // loop through positions array once to push all the values from shift to end of positions array.
    for (let k = 0; k < posArray.length - totalShift; k++) {
      subArr = posArray[k + totalShift]
      epoch = Math.floor(timeCounter)
      shiftedPosArray.push(epoch)
      subArr.forEach((value) => shiftedPosArray.push(value))
      timeCounter += timeStep
    }

    // loop through it a second time to push all the values from start to shift
    for (let i = posArray.length - totalShift; i < posArray.length; i++) {
      subArr = posArray[i - (posArray.length - totalShift)]
      epoch = Math.floor(timeCounter)
      shiftedPosArray.push(epoch)
      subArr.forEach((value) => shiftedPosArray.push(value))
      timeCounter += timeStep
    }

    return shiftedPosArray
  },

  createSampledPositionProperty(
    positionsArray,
    startDate,
    referenceFrame = Cesium.ReferenceFrame.FIXED
  ) {
    const sampledPositionProperty = new Cesium.SampledPositionProperty(
      referenceFrame
    )
    sampledPositionProperty.setInterpolationOptions(
      Cesium.LagrangePolynomialApproximation,
      5
    )
    sampledPositionProperty.addSamplesPackedArray(positionsArray, startDate)
    return sampledPositionProperty
  },

  getOrbitalPeriodFromSMA(sma) {
    return 2 * Math.PI * Math.sqrt(sma ** 3 / 3.986004415e14)
  }
}
